import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getAnalytics } from "firebase/analytics";

const placeholderTokens = ['your_', 'placeholder', 'changeme', 'example', '<', '>'];

const looksLikePlaceholder = (value: string): boolean => {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return true;
    return placeholderTokens.some((token) => normalized.includes(token));
};

const requiredEnv = (key: keyof ImportMetaEnv): string => {
    const value = (import.meta.env[key] || '').trim();
    if (!value) {
        throw new Error(`Missing required Firebase environment variable: ${key}`);
    }

    if (looksLikePlaceholder(value)) {
        throw new Error(`Invalid Firebase environment variable: ${key} appears to be a placeholder value.`);
    }

    if (key === 'VITE_FIREBASE_API_KEY' && !/^AIza[0-9A-Za-z_-]{20,}$/.test(value)) {
        throw new Error('Invalid Firebase API key format in VITE_FIREBASE_API_KEY.');
    }

    return value;
};

const firebaseConfig = {
    apiKey: requiredEnv('VITE_FIREBASE_API_KEY'),
    authDomain: requiredEnv('VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: requiredEnv('VITE_FIREBASE_PROJECT_ID'),
    storageBucket: requiredEnv('VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: requiredEnv('VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: requiredEnv('VITE_FIREBASE_APP_ID'),
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
export const app = initializeApp(firebaseConfig);

// Initialize Authentication
export const auth = getAuth(app);

// Initialize Firestore
export const db = getFirestore(app);

// Initialize other services
export const storage = getStorage(app);
export const functions = getFunctions(app);
export const analytics = typeof window !== 'undefined' ? getAnalytics(app) : null;
